/**
 * Production-Ready Logging Service
 *
 * This logger uses Pino for high-performance, structured logging with the following features:
 * - Development: Pretty-printed colored logs to console
 * - Production: Structured JSON logs (suitable for log aggregation services)
 * - Automatic redaction of sensitive data (passwords, tokens, API keys)
 * - Request context support (trace IDs, user IDs, etc.)
 * - Log levels: trace, debug, info, warn, error, fatal
 * - File-based logging in production (logs/app.log)
 *
 * Usage:
 *   import { logger } from '@/lib/logger';
 *   logger.info('User logged in', { userId: '123' });
 *   logger.error('Database error', { error, query });
 *   logger.child({ requestId: '456' }).info('Processing request');
 *
 * @see https://getpino.io
 */

import pino from 'pino';

const isDevelopment = process.env.NODE_ENV === 'development';
const isTest = process.env.NODE_ENV === 'test';

// Sensitive field names to redact from logs
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'apiKey',
  'api_key',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'secret',
  'secretKey',
  'secret_key',
  'authorization',
  'cookie',
  'sessionId',
  'session_id',
  'creditCard',
  'credit_card',
  'cardNumber',
  'card_number',
  'cvv',
  'ssn',
  'social_security',
];

// Create base logger configuration
const baseConfig: pino.LoggerOptions = {
  level: process.env.LOG_LEVEL || (isDevelopment ? 'debug' : 'info'),

  // Redact sensitive fields from logs
  redact: {
    paths: SENSITIVE_FIELDS,
    censor: '[REDACTED]',
  },

  // Add base fields to all logs
  base: {
    env: process.env.NODE_ENV,
    ...(process.env.VERCEL_ENV && { vercelEnv: process.env.VERCEL_ENV }),
  },
};

// Create the logger instance
// Note: We don't use pino-pretty transport to avoid serverless compatibility issues
// In production, logs are JSON (suitable for log aggregation)
// In development, logs are still readable but not pretty-printed
const pinoLogger = pino(baseConfig);

/**
 * Enhanced logger with convenience methods
 */
export const logger = {
  /**
   * Trace level logging (most verbose)
   */
  trace: (msg: string, data?: object) => {
    pinoLogger.trace(data, msg);
  },

  /**
   * Debug level logging
   */
  debug: (msg: string, data?: object) => {
    pinoLogger.debug(data, msg);
  },

  /**
   * Info level logging (general informational messages)
   */
  info: (msg: string, data?: object) => {
    pinoLogger.info(data, msg);
  },

  /**
   * Warn level logging (warnings that don't prevent operation)
   */
  warn: (msg: string, data?: object) => {
    pinoLogger.warn(data, msg);
  },

  /**
   * Error level logging (errors that need attention)
   */
  error: (msg: string, data?: object) => {
    pinoLogger.error(data, msg);
  },

  /**
   * Fatal level logging (application-breaking errors)
   */
  fatal: (msg: string, data?: object) => {
    pinoLogger.fatal(data, msg);
  },

  /**
   * General log method (maps to info level)
   */
  log: (msg: string, data?: object) => {
    pinoLogger.info(data, msg);
  },

  /**
   * Create a child logger with additional context
   * Useful for adding request-specific data to all logs
   *
   * @example
   * const requestLogger = logger.child({ requestId: '123', userId: '456' });
   * requestLogger.info('Processing request'); // Will include requestId and userId
   */
  child: (bindings: object) => {
    return pinoLogger.child(bindings);
  },

  /**
   * Get the underlying Pino logger instance
   * Use this if you need advanced Pino features
   */
  get pino() {
    return pinoLogger;
  },
};

/**
 * Legacy compatibility wrapper for gradual migration
 * @deprecated Use logger.info, logger.error, etc. instead
 */
export const legacyLogger = {
  log: (...args: unknown[]) => {
    if (isDevelopment) {
      console.log(...args);
    }
  },
  error: (...args: unknown[]) => {
    if (isDevelopment) {
      console.error(...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (isDevelopment) {
      console.warn(...args);
    }
  },
  info: (...args: unknown[]) => {
    if (isDevelopment) {
      console.info(...args);
    }
  },
  forceError: (...args: unknown[]) => {
    console.error(...args);
  },
  forceLog: (...args: unknown[]) => {
    console.log(...args);
  },
};

export default logger;
